import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  People as UsersIcon,
  Group as GroupsIcon,
  AccountTree as OUsIcon,
} from '@mui/icons-material';
import { diagnosticsAPI, usersAPI, groupsAPI, ousAPI } from '../services/api';

function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [systemStatus, setSystemStatus] = useState(null);
  const [counts, setCounts] = useState({ users: 0, groups: 0, ous: 0 });

  useEffect(() => {
    loadDashboardData();

    // Listen for sudo session established event
    const handleSudoSessionEstablished = () => {
      console.log('Sudo session established - refreshing dashboard data');
      loadDashboardData();
    };

    window.addEventListener('sudo-session-established', handleSudoSessionEstablished);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('sudo-session-established', handleSudoSessionEstablished);
    };
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    setError('');

    try {
      const [statusRes, usersRes, groupsRes, ousRes] = await Promise.all([
        diagnosticsAPI.getStatus().catch(() => ({ data: null })),
        usersAPI.list({ page_size: 1 }).catch(() => ({ data: { count: 0 } })),
        groupsAPI.list().catch(() => ({ data: { count: 0 } })),
        ousAPI.list().catch(() => ({ data: { count: 0 } })),
      ]);

      setSystemStatus(statusRes.data?.status || null);
      setCounts({
        users: usersRes.data.count || 0,
        groups: groupsRes.data.count || 0,
        ous: ousRes.data.count || 0,
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 1 }}>
          Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Overview of your Active Directory environment
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={{ xs: 2, sm: 3 }}>
        <Grid item xs={12} sm={6} lg={4}>
          <Card
            sx={{
              background: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, rgba(34, 167, 240, 0.1) 0%, rgba(34, 167, 240, 0.05) 100%)'
                  : 'linear-gradient(135deg, rgba(8, 119, 166, 0.08) 0%, rgba(8, 119, 166, 0.03) 100%)',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'primary.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <UsersIcon sx={{ fontSize: 32 }} />
                </Box>
              </Box>
              <Typography variant="h3" sx={{ mb: 0.5, fontWeight: 700 }}>
                {counts.users}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Total Users
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={4}>
          <Card
            sx={{
              background: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, rgba(255, 138, 61, 0.1) 0%, rgba(255, 138, 61, 0.05) 100%)'
                  : 'linear-gradient(135deg, rgba(255, 110, 0, 0.08) 0%, rgba(255, 110, 0, 0.03) 100%)',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'secondary.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <GroupsIcon sx={{ fontSize: 32 }} />
                </Box>
              </Box>
              <Typography variant="h3" sx={{ mb: 0.5, fontWeight: 700 }}>
                {counts.groups}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Total Groups
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={4}>
          <Card
            sx={{
              background: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, rgba(0, 200, 120, 0.1) 0%, rgba(0, 200, 120, 0.05) 100%)'
                  : 'linear-gradient(135deg, rgba(0, 168, 107, 0.08) 0%, rgba(0, 168, 107, 0.03) 100%)',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'success.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <OUsIcon sx={{ fontSize: 32 }} />
                </Box>
              </Box>
              <Typography variant="h3" sx={{ mb: 0.5, fontWeight: 700 }}>
                {counts.ous}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Organizational Units
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {systemStatus && (
          <Grid item xs={12}>
            <Paper sx={{ p: { xs: 3, md: 4 }, borderRadius: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                System Status
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="body1" sx={{ mr: 2, fontWeight: 600 }}>
                        Samba Service:
                      </Typography>
                      <Chip
                        icon={
                          systemStatus.samba_running ? (
                            <CheckIcon />
                          ) : (
                            <ErrorIcon />
                          )
                        }
                        label={systemStatus.samba_running ? 'Running' : 'Stopped'}
                        color={systemStatus.samba_running ? 'success' : 'error'}
                        size="small"
                      />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                        Domain
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {systemStatus.domain || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                        Realm
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {systemStatus.realm || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                        Server Role
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {systemStatus.server_role || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                        Forest Level
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {systemStatus.forest_level || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                        Domain Level
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {systemStatus.domain_level || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

export default Dashboard;
